import ErrorBoundary from '@hrms/common/component/error-boundary';
import { Drawer } from '@zeptonow/zui';
import React, { useCallback, useState } from 'react';
import SideDrawerContext from './sideDrawer.context';
import { DrawerItemProp, DrawerItemType, SideDrawerConfig } from './types';

const DrawerItem: React.FC<DrawerItemProp> = React.memo((props) => {
  const { id, config, closeSideDrawer, component: Component } = props;

  const handleClose = useCallback(() => {
    closeSideDrawer(id);
  }, [closeSideDrawer, id]);

  return (
    <Drawer
      key={id}
      open
      onClose={handleClose}
      anchor={config.anchor || 'right'}
      className={config.className}
    >
      <Component closeSideDrawer={handleClose} />
    </Drawer>
  );
});

const SideDrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [drawers, setDrawers] = useState<DrawerItemType[]>([]);

  const openSideDrawer = useCallback(
    (
      id: string,
      Component: React.FC<{ closeSideDrawer: () => void }>,
      config: SideDrawerConfig = {},
    ) => {
      setDrawers((prevDrawers) => [...prevDrawers, { id, Component, config }]);
    },
    [],
  );

  const closeSideDrawer = useCallback((id: string) => {
    setDrawers((prevDrawers) =>
      prevDrawers.filter((drawer) => drawer.id !== id),
    );
  }, []);

  return (
    <ErrorBoundary>
      <SideDrawerContext.Provider value={{ openSideDrawer, closeSideDrawer }}>
        {children}
        {drawers.map(({ id, Component, config }) => (
          <DrawerItem
            key={id}
            id={id}
            component={Component}
            config={config}
            closeSideDrawer={closeSideDrawer}
          />
        ))}
      </SideDrawerContext.Provider>
    </ErrorBoundary>
  );
};

export default SideDrawerProvider;
